import React from 'react';
import { Card, CardContent } from '@/admin/components/ui/card';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconEye,
  IconX,
  IconDeviceDesktop,
} from '@tabler/icons-react';
import { fetchDevices, type AdminDevice } from '@/admin/lib/api';

export const DevicesPage: React.FC = () => {
  const [devices, setDevices] = React.useState<AdminDevice[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedDevice, setSelectedDevice] =
    React.useState<AdminDevice | null>(null);

  const loadDevices = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const offset = (currentPage - 1) * pageSize;
      const result = await fetchDevices(pageSize, offset);
      setDevices(result.devices);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  React.useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto py-4 lg:py-6">
      <div className="shrink-0 px-4 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal">Devices</h1>
            <p className="text-sm text-muted-foreground">
              Review registered client devices and recent activity.
            </p>
          </div>
        </div>
      </div>

      <div className="shrink-0 px-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">
              Total: {total} devices
            </span>
          </div>

          {/* Pagination Controls */}
          {!loading && total > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {startIndex + 1}-{endIndex} of {total}
              </span>
              <Select
                value={pageSize.toString()}
                onValueChange={value => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  <IconChevronsLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <IconChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <IconChevronRight className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  <IconChevronsRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <Card>
          <CardContent className="p-4">
            <div className="overflow-hidden rounded-md border">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device Name</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>App Version</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[80px] text-right">
                      Details
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-32 text-center text-muted-foreground"
                      >
                        Loading devices...
                      </TableCell>
                    </TableRow>
                  )}
                  {error && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-32 text-center text-destructive"
                      >
                        {error}
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && !error && devices.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="h-32 text-center text-muted-foreground"
                      >
                        No devices yet
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    !error &&
                    devices.map(device => (
                      <TableRow key={device.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <IconDeviceDesktop className="size-4 text-muted-foreground" />
                            <span className="font-medium">{device.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{device.platform}</Badge>
                        </TableCell>
                        <TableCell>
                          {device.app_version || (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">
                              {formatRelativeTime(device.last_seen)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(device.last_seen)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">
                              {formatRelativeTime(device.created_at)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(device.created_at)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedDevice(device)}
                          >
                            <IconEye className="size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Modal */}
      {selectedDevice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setSelectedDevice(null)}
        >
          <div
            className="mx-4 max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg bg-card p-6 shadow-lg"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold">Device Details</h2>
                <p className="text-sm text-muted-foreground">
                  ID: {selectedDevice.id}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDevice(null)}
              >
                <IconX className="size-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Device Name
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <IconDeviceDesktop className="size-4 text-muted-foreground" />
                  <span>{selectedDevice.name}</span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Platform
                </div>
                <div className="mt-1">
                  <Badge variant="secondary">{selectedDevice.platform}</Badge>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  App Version
                </div>
                <div className="mt-1">
                  {selectedDevice.app_version || (
                    <span className="text-muted-foreground">Unknown</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  User ID
                </div>
                <div className="mt-1 font-mono text-sm">
                  {selectedDevice.user_id}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Last Seen
                </div>
                <div className="mt-1">
                  {formatDateTime(selectedDevice.last_seen)}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">
                  Created At
                </div>
                <div className="mt-1">
                  {formatDateTime(selectedDevice.created_at)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
